import React, {useState, useEffect} from 'react';
import {useNavigate, Link} from 'react-router-dom';
import {getAuth, createUserWithEmailAndPassword, updateProfile, signOut} from 'firebase/auth';
import {getFirestore, doc, setDoc} from 'firebase/firestore';
import {Snackbar, Alert} from '@mui/material';
import {
    TextField,
    Button,
    FormControlLabel,
    Checkbox,
    Select,
    MenuItem,
    FormControl,
    InputLabel,
    ListItemText,
    OutlinedInput,
    Box,
    Typography,
    InputAdornment,
    IconButton,
    Grid,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import styles from './Registration.module.css';
import { useAuth } from '../context/AuthContext';

const PROFESSIONS = [
    'Psychiatrist',
    'Psychologist',
    'Licensed Clinical Social Worker (LCSW)',
    'Licensed Professional Counselor (LPC)',
    'Marriage and Family Therapist (MFT)',
    'Mental Health Nurse Practitioner',
    'Other'
];

const THERAPY_STYLES = [
    'Cognitive Behavioral Therapy (CBT)',
    'Dialectical Behavior Therapy (DBT)',
    'Psychodynamic Therapy',
    'Acceptance and Commitment Therapy (ACT)',
    'Existential Therapy',
    'Eye Movement Desensitization and Reprocessing (EMDR)',
    'Mindfulness-Based Cognitive Therapy (MBCT)',
    'Gestalt Therapy',
    'Motivational Interviewing',
    'Person-Centered Therapy',
    'Eclectic Therapy',
    'Reality Therapy',
    'Narrative Therapy',
    'Emotionally Focused Therapy (EFT)'
];

const EDUCATION_LEVELS = [
    'Bachelor\'s Degree',
    'Master\'s Degree',
    'Doctoral Degree (PhD, PsyD)',
    'Medical Degree (MD, DO)',
    'Other'
];

const Registration = () => {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [profession, setProfession] = useState('');
    const [therapyStyles, setTherapyStyles] = useState([]);
    const [highestEducation, setHighestEducation] = useState('');
    const [professionalOrganizations, setProfessionalOrganizations] = useState('');
    const [agreeTerms, setAgreeTerms] = useState(false);
    const [error, setError] = useState('');
    const [successMessage, setSuccessMessage] = useState('');
    const [openSnackbar, setOpenSnackbar] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [passwordRequirements, setPasswordRequirements] = useState({
        length: false,
        uppercaseLowercase: false,
        number: false,
        specialChar: false
    });
    const navigate = useNavigate();
    const { currentUser, loading: authLoading } = useAuth();

    useEffect(() => {
        if (!authLoading && currentUser) {
            console.log("Registration: User already logged in, redirecting to dashboard.");
            navigate('/dashboard', { replace: true });
        }
    }, [currentUser, authLoading, navigate]);

    useEffect(() => {
        checkPasswordRequirements(password);
    }, [password]);

    const checkPasswordRequirements = (password) => {
        setPasswordRequirements({
            length: password.length >= 8,
            uppercaseLowercase: /(?=.*[a-z])(?=.*[A-Z])/.test(password),
            number: /\d/.test(password),
            specialChar: /[!@#$%^&*(),.?":{}|<>]/.test(password)
        });
    };

    const handleTogglePasswordVisibility = () => {
        setShowPassword(!showPassword);
    };

    const handleRegister = async (e) => {
        e.preventDefault();
        if (!agreeTerms) {
            setError('You must agree to the terms and attest to being a mental health professional.');
            return;
        }

        try {
            // Get user's IP address
            const ipResponse = await fetch('https://api.ipify.org?format=json');
            const ipData = await ipResponse.json();
            const signup_IP = ipData.ip;

            // Create Firebase user
            const auth = getAuth();
            const userCredential = await createUserWithEmailAndPassword(auth, email, password);
            const user = userCredential.user;

            // Update user profile
            await updateProfile(user, {
                displayName: `${firstName} ${lastName}`
            });

            // Save additional user data to Firestore
            const db = getFirestore();
            await setDoc(doc(db, 'Psyntel', user.uid, 'user_info', 'info'), {
                firstName,
                lastName,
                email,
                profession,
                therapyStyles,
                highestEducation,
                professionalOrganizations,
                signup_IP,
                createdAt: new Date(),
            }, { merge: true });

            // Create Stripe customer and subscription
            const idToken = await user.getIdToken();
            const response = await fetch('/api/subscription/create-customer', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${idToken}`
                },
                body: JSON.stringify({
                    email,
                    firstName,
                    lastName
                })
            });
            
            if (!response.ok) {
                throw new Error('Failed to create Stripe customer');
            }
            
            const stripeData = await response.json();
            console.log('Stripe subscription created:', stripeData);
            
            // Set success message and show snackbar
            setSuccessMessage('Success! Your 30-day free trial has started. Redirecting to login...');
            setOpenSnackbar(true);

            // Explicitly sign out the user before redirecting
            await signOut(auth);
            
            // Navigate immediately to login, replacing the current entry in history
            navigate('/login', { replace: true });

        } catch (error) {
            console.error('Registration or Stripe error:', error);
            // Improved error handling: Check the specific error type if possible
            if (error.code === 'auth/email-already-in-use') {
                 setError('This email address is already registered. Please login or use a different email.');
            } else if (error.code === 'auth/weak-password') {
                 setError('Password is too weak. Please choose a stronger password.');
            } else if (error.message === 'Failed to create Stripe customer') { // Check specific error message
                setError('Your account was created, but we had trouble starting your trial. Please contact support (team@psyntel.com).');
            } else {
                setError(error.message || 'Registration failed. Please try again.');
            }
            // Ensure snackbar doesn't show on error
            setOpenSnackbar(false); 
        }
    };

    const handleCloseSnackbar = (event, reason) => {
        if (reason === 'clickaway') {
            return;
        }
        setOpenSnackbar(false);
    };

    const isFormValid = agreeTerms &&
        Object.values(passwordRequirements).every(req => req) &&
        email && firstName && lastName && profession && therapyStyles.length > 0;

    if (authLoading) {
        return <div className={styles['R-loading']}>Loading user status...</div>;
    }

    if (currentUser) {
        return null;
    }

    return (
        <div className={styles['R-registration-wrapper']}>

            <Box className={styles['R-registration-page']}>
                <Box className={styles['R-main-content']}>
                    <Box className={styles['R-container']}>
                        <Box className={styles['R-header']}>
                            <img
                                src="https://firebasestorage.googleapis.com/v0/b/psyntel.appspot.com/o/psyntel_concept.png?alt=media&token=53b59fe7-acc5-4260-9881-e6eb28bc5af0"
                                alt="Psyntel Logo" className={styles['R-logo']}/>
                        </Box>
                        <Box className={styles['R-trial-info']}>
                            <Typography variant="h6" gutterBottom>Start Your Free Trial</Typography>
                            <Typography variant="body2" color="text.secondary">
                                ✓ 30-day free trial with full access
                                <br/>
                                ✓ No credit card required during trial
                                <br/>
                                ✓ Cancel anytime during trial period
                            </Typography>
                        </Box>
                        <form onSubmit={handleRegister}>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="First Name"
                                        value={firstName}
                                        onChange={(e) => setFirstName(e.target.value)}
                                        required
                                        fullWidth
                                        margin="normal"
                                        className={styles['R-form-control']}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Last Name"
                                        value={lastName}
                                        onChange={(e) => setLastName(e.target.value)}
                                        required
                                        fullWidth
                                        margin="normal"
                                        className={styles['R-form-control']}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Email"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        required
                                        fullWidth
                                        margin="normal"
                                        className={styles['R-form-control']}
                                    />
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Password"
                                        type={showPassword ? 'text' : 'password'}
                                        value={password}
                                        onChange={(e) => setPassword(e.target.value)}
                                        required
                                        fullWidth
                                        margin="normal"
                                        className={styles['R-form-control']}
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <IconButton
                                                        aria-label="toggle password visibility"
                                                        onClick={handleTogglePasswordVisibility}
                                                        edge="end"
                                                    >
                                                        {showPassword ? <VisibilityOff /> : <Visibility />}
                                                    </IconButton>
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                            </Grid>
                            <Box className={styles['R-password-requirements']}>
                                <Typography variant="body2">Your password must contain:</Typography>
                                <ul>
                                    <li className={passwordRequirements.length ? styles.met : ''}>At least 8
                                        characters
                                    </li>
                                    <li className={passwordRequirements.uppercaseLowercase ? styles.met : ''}>At least 1
                                        upper and lowercase letter
                                    </li>
                                    <li className={passwordRequirements.number ? styles.met : ''}>At least 1 number</li>
                                    <li className={passwordRequirements.specialChar ? styles.met : ''}>At least 1
                                        special character
                                    </li>
                                </ul>
                            </Box>
                            <Grid container spacing={2}>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="normal" className={styles['R-form-control']}>
                                        <InputLabel>Profession</InputLabel>
                                        <Select
                                            value={profession}
                                            onChange={(e) => setProfession(e.target.value)}
                                            required
                                            input={<OutlinedInput label="Profession" />}
                                        >
                                            {PROFESSIONS.map((prof) => (
                                                <MenuItem key={prof} value={prof}>{prof}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="normal" className={styles['R-form-control']}>
                                        <InputLabel>Therapy Styles</InputLabel>
                                        <Select
                                            multiple
                                            value={therapyStyles}
                                            onChange={(e) => setTherapyStyles(e.target.value)}
                                            input={<OutlinedInput label="Therapy Styles"/>}
                                            renderValue={(selected) => selected.join(', ')}
                                        >
                                            {THERAPY_STYLES.map((style) => (
                                                <MenuItem key={style} value={style}>
                                                    <Checkbox checked={therapyStyles.indexOf(style) > -1}/>
                                                    <ListItemText primary={style}/>
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <FormControl fullWidth margin="normal" className={styles['R-form-control']}>
                                        <InputLabel>Highest Level of Education</InputLabel>
                                        <Select
                                            value={highestEducation}
                                            onChange={(e) => setHighestEducation(e.target.value)}
                                            input={<OutlinedInput label="Highest Level of Education" />}
                                        >
                                            {EDUCATION_LEVELS.map((level) => (
                                                <MenuItem key={level} value={level}>{level}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} sm={6}>
                                    <TextField
                                        label="Professional Organizations"
                                        value={professionalOrganizations}
                                        onChange={(e) => setProfessionalOrganizations(e.target.value)}
                                        fullWidth
                                        margin="normal"
                                        className={styles['R-form-control']}
                                    />
                                </Grid>
                            </Grid>
                            <FormControlLabel
                                control={<Checkbox checked={agreeTerms}
                                                   onChange={(e) => setAgreeTerms(e.target.checked)}/>}
                                label={<>I agree to the Psyntel Inc. <a href="https://www.psyntel.com/Terms-&-Condition"
                                                                        target="_blank" rel="noopener noreferrer"
                                                                        className={styles['R-hyperlink-blue']}>Terms of
                                    Service</a>, <a href="https://www.psyntel.com/Privacy-Policy" target="_blank"
                                                       rel="noopener noreferrer" className={styles['R-hyperlink-blue']}>Privacy
                                    Policy</a>, and <a href="https://www.psyntel.com/baa" target="_blank"
                                                       rel="noopener noreferrer" className={styles['R-hyperlink-blue']}>Business Associate Agreement</a>, and attest that I am a licensed mental health professional or trainee.</>}
                                className={styles['R-form-check']}
                            />
                            {error && <Typography color="error">{error}</Typography>}


                            <Box className={styles['R-trial-note']}>
                                <Typography variant="body2" color="text.secondary" align="center">
                                    By clicking "Start Free Trial", you'll get immediate access to all features.
                                    We'll remind you before your trial ends - no automatic charges.
                                </Typography>
                            </Box>

                            <Button type="submit" variant="contained" color="primary" fullWidth disabled={!isFormValid}
                                    className={styles['R-btn-primary']}>
                                Start Free Trial
                            </Button>
                        </form>
                        <Box className={styles['R-login-footer']}>
                            <Typography>Already have an account? <Link to="/login"
                                                                       className={styles['R-hyperlink-blue']}>Login</Link></Typography>
                        </Box>
                    </Box>
                </Box>
                <Snackbar open={openSnackbar} autoHideDuration={6000} onClose={handleCloseSnackbar}>
                    <Alert onClose={handleCloseSnackbar} severity="success" sx={{width: '100%'}}>
                        {successMessage}
                    </Alert>
                </Snackbar>
            </Box>
        </div>
    );
};

export default Registration;
